import { Alert, AlertColor, AlertTitle, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Paper, Snackbar, Stack, Typography } from "@mui/material"
import { GridColDef, DataGrid, GridRowParams } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { doCancel, doDownload, doUpload, getCompanies, unDoCancel, xlsx2Json } from "./functions";
import { CompanyDataColumns, ContractStatus } from "./types";
import { isEmpty, isNil } from "lodash/fp";
import log from "loglevel";
import { MuiFileInput } from "mui-file-input";
import { inspect } from "./inspect";
import alertPropaties from './alert-propaties.json'


export const AdminCompanies = () => {
  const [selectedRowId, setSelectedRowId] = useState<string>("")
  const [rows, setRows] = useState<CompanyDataColumns[]>([])

  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadFileIsExcelFile, setUploadFileIsExcelFile] = useState<boolean>(false)
  const [open, setOpen] = useState<boolean>(false)
  const [alertStatus, setAlertStatus] = useState<AlertColor | undefined>(undefined)
  const [isDownloading, setIsDownloading] = useState<boolean>(false)
  const [isCanceling, setIsCanceling] = useState<boolean>(false)
  const [isUndoCanceling, setIsUndoCanceling] = useState<boolean>(false)
  const [targetCompanyContractStatus, setTargetCompanyContractStatus] = useState<ContractStatus>(ContractStatus.active)


  useEffect(() => {
    getCompanies().then((result) => {
      if (!isNil(result)) {
        setRows(result)
      }
    }).catch(log.error)
  }, [])

  const handleCancelClick = () => {
    setIsCanceling(true)
    if (!isEmpty(selectedRowId)) {
      doCancel(selectedRowId, rows).then((result) => {
        if (!isNil(result)) {
          getCompanies().then((rows) => {
            if (!isNil(rows)) {
              setRows(rows)
              setTargetCompanyContractStatus(ContractStatus.inactive)
            }
          }).catch(log.error)
        }
      })
    }
    setIsCanceling(false)
  }

  const handleDownloadClick = async () => {
    setIsDownloading(true)
    if (!isEmpty(selectedRowId)) {
      const row = rows.find((row) => row.id === selectedRowId)
      if (!isNil(row)) {
        await doDownload(selectedRowId, 'EmployeeRegistForm.xlsx', row.name)
      }
    }
    setIsDownloading(false)
  }

  const handleUnCancelClick = () => {
    setIsUndoCanceling(true)
    if (!isEmpty(selectedRowId)) {
      unDoCancel(selectedRowId, rows).then((result) => {
        if (!isNil(result)) {
          getCompanies().then((rows) => {
            if (!isNil(rows)) {
              setRows(rows)
              setTargetCompanyContractStatus(ContractStatus.active)
            }
          }).catch(log.error)
        }
      })
    }
    setIsUndoCanceling(false)
  }

  const handleUploadClick = () => {
    setOpen(true)
  }
  const handleClose = () => {
    setUploadFile(null)
    setOpen(false)
  }

  const handleSubmit = async (uploadFile: File | null) => {
    if (!isNil(uploadFile)) {
      const row = rows.find((row) => row.id === selectedRowId) as CompanyDataColumns
      const { name: companyName } = row
      const memberId = selectedRowId.split('/')[0]
      if (!isNil(memberId) && !isNil(companyName)) {
        const data = await xlsx2Json(uploadFile, "入力表")
        doUpload(memberId, data, companyName).then((result) => {
          if (!isNil(result)) {
            setAlertStatus("success")
            getCompanies().then((result) => {
              if (!isNil(result)) {
                setRows(result)
              }
            }).catch(log.error)
          } else {
            setAlertStatus("error")
          }
        }).catch((error) => {
          log.error(`[index.handleSubmit] Upload failed.`, inspect({ error }))
          setAlertStatus("error")
        })
      }
    } else {
      setAlertStatus("error")
    }
    handleClose()
  }

  const handleInput = (uploadFile: File | null) => {
    setUploadFile(uploadFile)
    if (!isNil(uploadFile) && (uploadFile.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")) {
      setUploadFileIsExcelFile(true)
    } else {
      setUploadFileIsExcelFile(false)
    }
  }
  const handleAlertClose = () => {
    setAlertStatus(undefined)
  }

  const columns: GridColDef[] = [
    { field: 'id', headerName: '会員ID', width: 300 },
    { field: 'name', headerName: '会社名', width: 300 },
    { field: 'code', headerName: '企業コード', width: 200 },
    { field: 'closingDate', headerName: '締日', width: 200 },
    { field: 'autoPayment', headerName: '自動請求', width: 200 },
    { field: 'contractStatus', headerName: '契約状態', width: 200 },
    { field: 'sub', headerName: 'TCID識別子', width: 500 }
  ]

  const paginationModel = { page: 0, pageSize: 50 }
  return (
    <Box>
      <Typography variant="h6" borderBottom={2} borderColor={"GrayText"} color="gray">従業員情報一覧</Typography>
      <Paper sx={{ height: 400, width: '100%' }}>
        <DataGrid
          loading={isEmpty(rows)}
          rows={rows}
          columns={columns}
          initialState={{ pagination: { paginationModel }, sorting: { sortModel: [{ field: 'contractStatus', sort: 'asc' }] } }}
          pageSizeOptions={[5, 10]}
          sx={{ border: 0 }}
          onRowClick={(event: GridRowParams<CompanyDataColumns>) => {
            setSelectedRowId(event.id.toString())
            setTargetCompanyContractStatus(event.row.contractStatus)
          }}
        />
      </Paper>
      <Stack direction={"row"} spacing={3} justifyContent={"center"} marginTop={3}>
        <Button variant="contained" onClick={handleDownloadClick} disabled={isEmpty(selectedRowId) || isDownloading}>従業員ファイルダウンロード{isDownloading ? <CircularProgress size={20} color="inherit" /> : ""}</Button>
        <Button variant="contained" onClick={handleCancelClick} disabled={isEmpty(selectedRowId) || isCanceling || targetCompanyContractStatus === ContractStatus.inactive}>解約{isCanceling ? <CircularProgress size={20} color="inherit" /> : ""}</Button>
        <Button variant="outlined" onClick={handleUnCancelClick} disabled={isEmpty(selectedRowId) || isUndoCanceling || targetCompanyContractStatus === ContractStatus.active}>解約解除{isUndoCanceling ? <CircularProgress size={20} color="inherit" /> : ""}</Button>
        <Button variant="contained" onClick={handleUploadClick} disabled={isEmpty(selectedRowId)}>従業員ファイルアップロード</Button>
      </Stack>
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          component: 'form',
          onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            handleClose()
          }
        }}
      >
        <DialogTitle>ファイルアップロード</DialogTitle>
        <DialogContent>
          <DialogContentText>
            従業員情報を記載したExcelファイルを選択してください。
          </DialogContentText>
          <MuiFileInput value={uploadFile} onChange={handleInput} variant="outlined" />
          <br />
          <Typography variant="caption" component="div" gutterBottom>xlsx ファイルのみ、ファイルサイズは5MB以内。</Typography>
          {!(uploadFile) && (
            <Typography variant="caption" component="div" color="error.main" gutterBottom>ファイルを選択してください。</Typography>)}
          {(uploadFile) && !uploadFileIsExcelFile && (
            <Typography variant="caption" component="div" color="error.main" gutterBottom>このファイルタイプはサポートしていません。</Typography>)}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>キャンセル</Button>
          <Button type="submit" onClick={async () => handleSubmit(uploadFile)} disabled={isNil(uploadFile) || !uploadFileIsExcelFile}>送信</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={!isEmpty(alertStatus)} autoHideDuration={5000} onClose={handleAlertClose}>
        <Alert severity={alertStatus}>
          <AlertTitle>{!isEmpty(alertStatus) ? alertPropaties[alertStatus as keyof typeof alertPropaties]["alertTitle"] : ""}</AlertTitle>
          {!isEmpty(alertStatus) ? alertPropaties[alertStatus as keyof typeof alertPropaties]["alertMessage"] : ""}
        </Alert>
      </Snackbar>
    </Box>
  )
}